import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_LABELS, KANBAN_COLUMNS } from "@/lib/constants";
import { Table2, Kanban, Map as MapIcon, FileText, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import SignsMap from "@/components/SignsMap";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

const COLORS = ["#000091", "#E1000F", "#FFD600", "#059669", "#7c3aed", "#0891b2"];

export default function MunicipalityDashboard() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("table");

  useEffect(() => {
    api.get("/applications").then((r) => setApps(r.data));
    api.get("/analytics").then((r) => setStats(r.data));
  }, []);

  const kpi = (label, value, Icon, color = "#000091") => (
    <div className="card-flat p-5">
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span><Icon size={18} style={{ color }} /></div>
      <div className="text-3xl font-black font-display mt-2">{value}</div>
    </div>
  );

  const pieData = stats ? Object.entries(stats.by_status).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v })) : [];
  const barData = pieData.slice(0, 6);

  return (
    <div className="max-w-7xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Espace mairie</div>
        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Instruction des demandes</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpi("Demandes totales", stats.total, FileText)}
          {kpi("À traiter", stats.pending, Clock, "#d97706")}
          {kpi("Taux conformité", `${stats.compliance_rate}%`, CheckCircle2, "#059669")}
          {kpi("Refusées", stats.rejected, AlertTriangle, "#E1000F")}
        </div>
      )}

      {stats && (
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <div className="card-flat p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Répartition par statut</div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>{pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Volume par statut</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData}><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#000091" /></BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {[["table", Table2, "Tableau"], ["kanban", Kanban, "Kanban"], ["map", MapIcon, "Carte"]].map(([v, Icon, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border transition-colors ${view === v ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}><Icon size={16} /> {l}</button>
        ))}
      </div>

      {view === "table" && (
        <div className="card-flat divide-y divide-border">
          {apps.length === 0 && <div className="p-8 text-center text-slate-500">Aucune demande.</div>}
          {apps.map((a) => (
            <Link key={a.id} to={`/app/dossier/${a.id}`} data-testid={`muni-row-${a.reference}`} className="flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition-colors">
              <div><div className="font-semibold">{a.commercial?.business_name || a.merchant_name}</div><div className="text-sm text-slate-500 font-mono">{a.reference} · {a.sign_type}</div></div>
              <div className="flex items-center gap-4">{a.precheck && <span className="text-sm font-bold font-display">{a.precheck.score}</span>}<StatusBadge status={a.status} /></div>
            </Link>
          ))}
        </div>
      )}

      {view === "kanban" && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 overflow-x-auto">
          {KANBAN_COLUMNS.map((col) => (
            <div key={col} className="min-w-[180px]">
              <div className="text-xs font-bold uppercase tracking-wide mb-2 px-1">{STATUS_LABELS[col]} <span className="text-slate-400">({apps.filter((a) => a.status === col).length})</span></div>
              <div className="space-y-2">
                {apps.filter((a) => a.status === col).map((a) => (
                  <Link key={a.id} to={`/app/dossier/${a.id}`} className="block card-flat p-3 hover:border-[#000091] transition-colors">
                    <div className="font-semibold text-sm truncate">{a.commercial?.business_name || a.merchant_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{a.reference}</div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === "map" && <SignsMap applications={apps} />}
    </div>
  );
}
